import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, refreshDownloadToken, startDownloadTokenRefresh, stopDownloadToken, lijst } from "@/lib/api";
import { toast } from "sonner";
import { rollenVan } from "@/lib/rollen";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [permissies, setPermissies] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadPermissies = useCallback(async () => {
    try {
      const { data } = await api.get("/mijn/permissies");
      setPermissies({ ...(data && typeof data === "object" ? data : {}), modules_lezen: lijst(data?.modules_lezen), modules_bewerken: lijst(data?.modules_bewerken), modules_beheren: lijst(data?.modules_beheren) });
    } catch { setPermissies({ modules_lezen: [], modules_bewerken: [], modules_beheren: [] }); }
  }, []);

  useEffect(() => {
    (async () => {
      const token = localStorage.getItem("token");
      if (!token) { setUser(false); setLoading(false); return; }
      for (let poging = 0; poging < 4; poging++) {
        try {
          const { data } = await api.get("/auth/me");
          await refreshDownloadToken(); startDownloadTokenRefresh();
          setUser(data);
          if (data.role !== "onderaannemer") await loadPermissies();
          break;
        } catch (e) {
          // Alleen een échte 401 logt uit; 429/5xx/netwerk (drukte, herstart) houdt het token en probeert nog 3× (3 s)
          if (e?.response?.status === 401) { localStorage.removeItem("token"); setUser(false); break; }
          if (poging === 3) { setUser(false); break; }
          await new Promise((r) => setTimeout(r, 3000));
        }
      }
      setLoading(false);
    })();
  }, [loadPermissies]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", {
      email, password,
      trusted_device_token: localStorage.getItem("bpt_trusted_device") || undefined,
    });
    if (data.twofa_required) return data; // Login.jsx toont de 2FA-stap
    localStorage.setItem("token", data.token);
    Object.keys(sessionStorage).filter((k) => k.startsWith("id_verzoek_dismissed")).forEach((k) => sessionStorage.removeItem(k));
    await refreshDownloadToken(); startDownloadTokenRefresh();
    setUser(data);
    if (data.role !== "onderaannemer") await loadPermissies();
    return data;
  };

  // Na geslaagde 2FA-verificatie/-setup: sessie afronden
  const completeTwoFa = async (data) => {
    localStorage.setItem("token", data.token);
    Object.keys(sessionStorage).filter((k) => k.startsWith("id_verzoek_dismissed")).forEach((k) => sessionStorage.removeItem(k));
    await refreshDownloadToken(); startDownloadTokenRefresh();
    setUser(data);
    await loadPermissies();
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("token"); stopDownloadToken();
    setUser(false); setPermissies(null);
  };

  // Auto-uitlog na 45 min inactiviteit (veiligheid op gedeelde apparaten)
  useEffect(() => {
    if (!user) return;
    let timer;
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        logout();
        window.location.href = "/login?reden=inactiviteit";
      }, 45 * 60 * 1000);
    };
    const events = ["mousedown", "keydown", "touchstart", "scroll"];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, reset));
    };
    // eslint-disable-next-line
  }, [user]);

  // LIVE RECHTEN — poll elke 30s + bij tab-focus, zodat toegekende rechten
  // direct verschijnen zonder dat de medewerker de app hoeft te verversen
  useEffect(() => {
    if (!user) return;
    let bezig = false;
    const refresh = async () => {
      if (bezig) return;
      bezig = true;
      try {
        const [{ data: me }, { data: perms }] = await Promise.all([
          api.get("/auth/me"),
          api.get("/mijn/permissies"),
        ]);
        setPermissies((prev) => {
          const oud = prev?.modules_lezen || [];
          const nieuw = perms?.modules_lezen || [];
          const toegevoegd = nieuw.filter((m) => prev && !oud.includes(m));
          if (toegevoegd.length > 0) {
            toast.success(`Nieuwe toegang: ${toegevoegd.join(", ")}`, {
              description: "Je rechten zijn zojuist bijgewerkt — de modules staan nu in je menu.",
            });
          }
          return JSON.stringify(prev) === JSON.stringify(perms) ? prev : perms;
        });
        setUser((prev) => {
          if (!prev) return prev;
          const zelfde = prev.role === me.role &&
            JSON.stringify(prev.custom_permissies || []) === JSON.stringify(me.custom_permissies || []);
          return zelfde ? prev : { ...prev, ...me };
        });
      } catch {}
      bezig = false;
    };
    const iv = setInterval(refresh, 30000);
    const onVis = () => { if (document.visibilityState === "visible") refresh(); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onVis);
    return () => {
      clearInterval(iv);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onVis);
    };
    // eslint-disable-next-line
  }, [user?.id]);

  // Helpers
  const canRead = (module) => {
    if (!permissies) return true; // fallback tijdens laden
    if (user?.role === "manager_plus") return true;
    return (permissies.modules_lezen || []).includes(module);
  };
  const canEdit = (module) => {
    if (!permissies) return false;
    if (user?.role === "manager_plus") return true;
    return (permissies.modules_bewerken || []).includes(module);
  };
  const canBeheer = (module) => {
    if (!permissies) return user?.role === "manager_plus";
    if (user?.role === "manager_plus") return true;
    return (permissies.modules_beheren || []).includes(module);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, completeTwoFa, logout, loading, permissies, canRead, canEdit, canBeheer, reloadPermissies: loadPermissies }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

// Rol hiërarchie
export const ROLE_LEVEL = {
  manager_plus: 100, directie: 100,
  manager: 80, manager_operationeel: 80, manager_bedrijfsvoering: 80, manager_brandveiligheid: 80,
  manager_hr: 80, manager_algemeen: 80,
  teammanager: 60,
  kcc: 50, accountmanager: 50,
  buitendienst: 40, buitendienst_nl: 40, buitendienst_be: 40,
  salesteam: 30,
  projectleider_pb: 25,
  applicateur: 20,
  medewerker: 10,
};
// Voorstel 67: hoogste rang over alle rollen (rechten = optelsom)
export const roleLevel = (u) => Math.max(0, ...rollenVan(u).map((r) => ROLE_LEVEL[r] || 0));
export const hasFullView = (u) => roleLevel(u) >= 50;   // KCC/Accountmanager en hoger
export const isManagerRole = (u) => roleLevel(u) >= 60;  // teammanager en hoger
export const isManagerPlus = (u) => u?.role === "manager_plus";
